import {PropsWithChildren} from 'react';

import {useConfig} from '@/contexts/config';
import {useUserProfileContext} from '@/contexts/user-profile';
import {createValueResolver, ValueResolver} from '@/engine';
import {getAllocationsAsDocumentData} from '@/engine/plugins/predetermined-allocations/utils/get-allocations-as-document-data';
import {useSingleton} from '@/hooks/use-singleton';
import {FullDocument} from '@/interfaces/document';
import {Allocation} from '@/repositories/beneva-api/endpoints/allocations';
import {Attachment} from '@/repositories/beneva-api/endpoints/attachments';
import {UserProfile} from '@/repositories/beneva-api/endpoints/users';
import {mapDocumentAttachments} from '@/utils/document/attachments';
import {deserializeDocument} from '@/utils/document/serializer';
import {createSerializer} from '@/utils/document/value';
import {getUserProfileAsDocumentData} from '@/utils/user-profile';
import {useDocumentRepository} from '../repository';
import {createDocumentStore, DocumentStoreApi} from '../store/document-store';
import {DocumentUtilsContext} from './document-utils-context';

export const DocumentUtilsProvider = ({children}: PropsWithChildren) => {
  const {profile} = useUserProfileContext();
  const {model, domainValueResolver} = useConfig();
  const {document, allocations, attachments} = useDocumentRepository();

  const store = useSingleton(() =>
    initializeDocumentStore(domainValueResolver, document, profile, allocations, attachments),
  );
  const valueResolver = useSingleton(() => createValueResolver(store, model.values));
  const serialize = useSingleton(() => createSerializer(valueResolver));

  const value = {store, valueResolver, serialize};

  return <DocumentUtilsContext.Provider value={value}>{children}</DocumentUtilsContext.Provider>;
};

function initializeDocumentStore(
  initialResolver: ValueResolver,
  document: FullDocument,
  profile: UserProfile,
  allocations?: Allocation[],
  attachments?: Attachment[],
): DocumentStoreApi {
  const newDocument = deserializeDocument(document, initialResolver);

  return createDocumentStore({
    data: {
      ...getUserProfileAsDocumentData(profile),
      ...getAllocationsAsDocumentData(allocations),
      ...newDocument.data,
    },
    attachments: mapDocumentAttachments(attachments ?? []),
    meta: newDocument.meta,
  });
}
